import { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Linkedin, Film, ArrowRight } from "lucide-react";
import { useContent } from "../../context/ContentContext";
import { resolveUrl } from "../../lib/media";
import { Modal } from "./Modal";
import { Reveal } from "./Reveal";
import { SectionBg } from "./SectionBg";

const slugify = (s = "") => s.toString().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const hasDetail = (m) => !!(m.bio || m.filmography || m.instagram || m.linkedin || m.parcours || m.competences || (m.gallery || []).length);

const TeamMemberModal = ({ member, onClose }) => {
  const films = member ? (member.filmography || "").split("\n").map((s) => s.trim()).filter(Boolean) : [];
  return (
    <Modal open={!!member} onClose={onClose} size="xl" rowLayout testid="team-modal">
      {member && (
        <>
          {member.photo && <div className="md:w-2/5 h-56 sm:h-64 md:h-auto shrink-0 bg-[#0e0e0e]"><img src={resolveUrl(member.photo)} alt={member.name} className="w-full h-full object-cover" /></div>}
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-7 md:p-9" data-testid="team-modal-scroll" data-lenis-prevent>
            <div className="text-[10px] tracking-[0.3em] uppercase text-electric mb-2 pr-10">{member.role}</div>
            <h3 data-testid="team-modal-name" className="font-serif text-3xl md:text-4xl text-white mb-4">{member.name}</h3>
            {member.bio && <p className="text-base text-secondary2 font-light leading-relaxed whitespace-pre-line mb-6">{member.bio}</p>}
            {films.length > 0 && (
              <div className="mb-6">
                <div className="text-[10px] tracking-[0.3em] uppercase text-electric mb-3">Filmographie</div>
                <ul className="space-y-1.5">
                  {films.map((f, i) => <li key={i} className="flex items-start gap-2 text-sm text-white/85"><Film size={14} className="text-electric mt-0.5 shrink-0" /> {f}</li>)}
                </ul>
              </div>
            )}
            {(member.instagram || member.linkedin) && (
              <div className="flex flex-wrap gap-3">
                {member.instagram && <a href={member.instagram} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/15 text-white text-[11px] tracking-[0.1em] uppercase hover:border-electric hover:text-electric transition-colors"><Instagram size={15} /> Instagram</a>}
                {member.linkedin && <a href={member.linkedin} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/15 text-white text-[11px] tracking-[0.1em] uppercase hover:border-electric hover:text-electric transition-colors"><Linkedin size={15} /> LinkedIn</a>}
              </div>
            )}
            {member.profileEnabled !== false && (
              <Link to={`/equipe/${member.slug || slugify(member.name)}`} data-testid="team-modal-profile" className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-electric text-black text-[12px] tracking-[0.12em] uppercase font-semibold hover:bg-white transition-colors">
                Voir le profil <ArrowRight size={15} />
              </Link>
            )}
          </div>
        </>
      )}
    </Modal>
  );
};

export const Team = () => {
  const { content } = useContent();
  const t = content.team;
  const [active, setActive] = useState(null);
  if (!t || !t.members?.length) return null;
  return (
    <section id="team" className="relative overflow-hidden bg-[#050505] py-24 md:py-40 border-t border-white/10" data-testid="team-section">
      <SectionBg id="team" />
      <div className="relative z-10 max-w-[1500px] mx-auto px-6 md:px-12">
        <Reveal className="mb-16 md:mb-24 max-w-2xl">
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-10 bg-electric" />
            <span className="text-xs tracking-[0.35em] uppercase text-electric">{t.eyebrow}</span>
          </div>
          <h2 className="font-serif text-5xl md:text-7xl text-white tracking-tight leading-[0.95]">{t.heading}</h2>
        </Reveal>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {t.members.map((m, i) => {
            const clickable = hasDetail(m);
            const Card = clickable ? "button" : "div";
            return (
              <Reveal key={i} delay={i * 0.1}>
                <Card data-testid={`team-member-${i}`} onClick={clickable ? () => setActive(m) : undefined}
                  className={`group relative rounded-lg overflow-hidden w-full text-left ${clickable ? "cursor-pointer" : ""}`}>
                  <div className="relative h-[26rem] overflow-hidden">
                    <img src={resolveUrl(m.photo)} alt={m.name} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
                  </div>
                  <div className="absolute bottom-0 inset-x-0 p-6">
                    <div className="text-[10px] tracking-[0.3em] uppercase text-electric mb-2">{m.role}</div>
                    <h3 className="font-serif text-2xl text-white">{m.name}</h3>
                    {m.bio && <p className="mt-2 text-sm text-secondary2 font-light line-clamp-2">{m.bio}</p>}
                    {clickable && <span className="mt-3 inline-block text-[10px] tracking-[0.2em] uppercase text-electric opacity-0 group-hover:opacity-100 transition-opacity">Voir le profil →</span>}
                  </div>
                </Card>
              </Reveal>
            );
          })}
        </div>
      </div>
      <TeamMemberModal member={active} onClose={() => setActive(null)} />
    </section>
  );
};
